import { useCallback, useEffect, useState } from "react";
import {
  SOCKET_EVENT_WAITING_ROOM_JOIN,
  SOCKET_EVENT_WAITING_ROOM_LEAVE,
  SESSION_STATUS_WAITING,
} from "@icebergeradmin/common";
import { useSocketContext } from "@/context/socket.context/socket.context";
import { getWaitingRoomQueue } from "@/services/call-center/personal-room.service";
import { TWaitingRoomEntry } from "@/hooks/call-center/useWaitingRoom";
import { TJoinedClient } from "@/models/call.center.model";

// Post-refactor (see socket-events.md §4.2), the backend NO LONGER re-emits
// `waiting-room:admitted` / `waiting-room:denied` — those events are now
// agent→backend only. Local UI cleanup happens via the optimistic
// `removeEntry` exposed by `useWaitingRoom`; this hook just mirrors the
// join/leave stream.

/**
 * Listens to waiting-room socket events and maps entries to TJoinedClient[]
 * so they can be displayed in the call-center dashboard alongside regular leads.
 *
 * Note on device filtering: the personal-room IN-SESSION sidebar (driven by
 * `useWaitingRoom`) is desktop-only because admit-to-Zoom doesn't apply to
 * mobile leads. This hook, by contrast, feeds the DASHBOARD widget — and
 * the dashboard widget IS the entry point for the Twilio "Call Lead" flow
 * (scenario #6). So we DO surface mobile entries here. The widget itself
 * picks the right CTA per device type (Join Meeting for desktop, Call Lead
 * for mobile).
 */
export const useWaitingRoomLeads = (personalRoomSessionName: string) => {
  const socket = useSocketContext();
  const [entries, setEntries] = useState<TWaitingRoomEntry[]>([]);

  const removeEntry = useCallback((entryId: string) => {
    setEntries((prev) => prev.filter((e) => e.entryId !== entryId));
  }, []);

  // Initial fetch
  useEffect(() => {
    if (!personalRoomSessionName) return;

    getWaitingRoomQueue()
      .then(setEntries)
      .catch(() => {
        // Silently fail
      });
  }, [personalRoomSessionName]);

  // Socket listeners
  useEffect(() => {
    if (!personalRoomSessionName) return;

    const onJoin = (entry: TWaitingRoomEntry) => {
      setEntries((prev) => {
        if (prev.some((e) => e.entryId === entry.entryId)) return prev;
        return [...prev, entry];
      });
    };

    const onLeave = ({ entryId }: { entryId: string }) => removeEntry(entryId);

    socket.on(SOCKET_EVENT_WAITING_ROOM_JOIN, onJoin);
    socket.on(SOCKET_EVENT_WAITING_ROOM_LEAVE, onLeave);

    return () => {
      socket.off(SOCKET_EVENT_WAITING_ROOM_JOIN, onJoin);
      socket.off(SOCKET_EVENT_WAITING_ROOM_LEAVE, onLeave);
    };
  }, [personalRoomSessionName, socket, removeEntry]);

  // Map entries to TJoinedClient format so the dashboard can render them like any other lead
  const waitingRoomLeads: TJoinedClient[] = entries.map((entry) => ({
    email: entry.email,
    name: entry.name,
    role: 0,
    leadId: 0,
    sessionId: "",
    sessionName: personalRoomSessionName,
    sessionStatus: SESSION_STATUS_WAITING,
    startDate: new Date().toISOString(),
    deviceType: entry.deviceType,
    phoneNumber: entry.phoneNumber,
  }));

  return { waitingRoomLeads, removeEntry };
};
